import { useCallback, useState } from "react";

export interface SlashCommandState {
  isOpen: boolean;
  query: string;
  cursorPosition: number;
}

export interface UseSlashCommandsReturn {
  commandState: SlashCommandState;
  closeMenu: () => void;
  detectSlashCommand: (text: string, cursorPos: number) => void;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onInputClick: (event: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
}

export const useSlashCommands = (): UseSlashCommandsReturn => {
  const [commandState, setCommandState] = useState<SlashCommandState>({
    isOpen: false,
    query: "",
    cursorPosition: 0,
  });

  // Close command menu
  const closeMenu = useCallback(() => setCommandState((prev) => ({ ...prev, isOpen: false })), []);

  // Detect slash command in input
  const detectSlashCommand = useCallback(
    (text: string, cursorPos: number) => {
      // Find the last "/" before the cursor
      const textBeforeCursor = text.slice(0, cursorPos);
      const lastSlashIndex = textBeforeCursor.lastIndexOf("/");

      // Check if we're in a slash command context
      if (lastSlashIndex !== -1) {
        const textAfterSlash = textBeforeCursor.slice(lastSlashIndex + 1);

        // Only treat as command if there's no space after the slash (or we're still typing)
        if (!textAfterSlash.includes(" ")) {
          // Check what comes before the slash to avoid triggering on URLs/file paths
          const charBeforeSlash = lastSlashIndex > 0 ? textBeforeCursor[lastSlashIndex - 1] : "";

          // Only trigger if:
          // 1. Slash is at the start of input (lastSlashIndex === 0), OR
          // 2. Slash is preceded by whitespace (space, newline, tab)
          const isValidSlashCommand = lastSlashIndex === 0 || /\s/.test(charBeforeSlash);

          if (isValidSlashCommand) {
            setCommandState({
              isOpen: true,
              query: textAfterSlash,
              cursorPosition: lastSlashIndex,
            });
            return;
          }
        }
      }

      // Close the command menu if conditions aren't met
      closeMenu();
    },
    [closeMenu]
  );

  // Handle input change to detect slash commands
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const inputElement = event.target as HTMLTextAreaElement;
      const cursorPos = inputElement.selectionStart;
      const value = inputElement.value;
      detectSlashCommand(value, cursorPos);
    },
    [detectSlashCommand]
  );

  // Handle click in textarea to update cursor position for slash detection
  const handleInputClick = useCallback(
    (event: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const inputElement = event.target as HTMLTextAreaElement;
      const cursorPos = inputElement.selectionStart;
      const value = inputElement.value;
      detectSlashCommand(value, cursorPos);
    },
    [detectSlashCommand]
  );

  // Handle keyboard events in textarea
  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (commandState.isOpen) {
        // Prevent default behavior for navigation keys when menu is open
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          // Let the event bubble to the Command component
        }

        // Prevent default Enter behavior
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          // The Command component will handle the selection
        }
      }
    },
    [commandState]
  );

  return {
    commandState,
    detectSlashCommand,
    closeMenu,
    onInputChange: handleInputChange,
    onInputClick: handleInputClick,
    onInputKeyDown: handleInputKeyDown,
  };
};
